import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLang } from "../../../shared/i18n/useLang";
import AuthShell, { PasswordField, TextField, erroresDeCampo } from "../components/AuthShell";
import { aceptarInvitacion, getInvitacion } from "../services/accesoService";

/**
 * /invitacion/:token — el condómino crea su propio acceso al portal.
 *
 * La administración ya eligió la filial y el correo al invitarlo; acá él pone
 * su nombre, el usuario con el que va a entrar y su contraseña. Al terminar
 * vuelve al login con el usuario ya escrito.
 */
export default function InvitacionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const L = useLang();

  const [inv, setInv] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");

  const [form, setForm] = useState({ first_name: "", last_name: "", username: "", password: "", confirmar: "" });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    getInvitacion(token)
      .then((d) => {
        setInv(d);
        setForm((f) => ({ ...f, username: d.sugerencia_usuario || "" }));
      })
      .catch((e) => setErrorCarga(e.message))
      .finally(() => setCargando(false));
  }, [token]);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  function validar() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = L("Ingresá tu nombre.", "Enter your first name.");
    if (!/^[\w.@+-]{3,150}$/.test(form.username.trim()))
      e.username = L("Entre 3 y 150 caracteres, sin espacios (letras, números y . @ + - _).",
                     "3 to 150 characters, no spaces (letters, numbers and . @ + - _).");
    if (form.password.length < 8) e.password = L("Mínimo 8 caracteres.", "At least 8 characters.");
    if (form.confirmar !== form.password) e.confirmar = L("Las contraseñas no coinciden.", "Passwords do not match.");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function enviar(ev) {
    ev.preventDefault();
    setApiError("");
    if (!validar()) return;
    setEnviando(true);
    try {
      const r = await aceptarInvitacion(token, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        username: form.username.trim(),
        password: form.password,
      });
      navigate("/login", {
        replace: true,
        state: {
          username: r.username,
          aviso: L("¡Listo! Tu acceso quedó creado. Iniciá sesión con tu contraseña.",
                   "Done! Your access was created. Sign in with your password."),
        },
      });
    } catch (e) {
      if (e.status === 400 && e.data) setErrors(erroresDeCampo(e.data));
      else setApiError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  if (cargando) {
    return (
      <AuthShell>
        <p className="lp-form-sub" style={{ textAlign: "center", padding: "30px 0" }}>
          <i className="fa-solid fa-circle-notch lp-spin" /> {L("Cargando invitación…", "Loading invitation…")}
        </p>
      </AuthShell>
    );
  }

  if (errorCarga) {
    return (
      <AuthShell>
        <h2 className="lp-form-title">{L("Invitación no disponible", "Invitation unavailable")}</h2>
        <div className="lp-api-err" style={{ marginTop: 14 }}>
          <i className="fa-solid fa-circle-exclamation" />
          {errorCarga}
        </div>
        <Link to="/login" className="lp-link">
          <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
          {L("Ir a iniciar sesión", "Go to sign in")}
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="lp-chip">
        <i className="fa-solid fa-house-chimney" />
        {inv.etiqueta_filial} {inv.filial}
      </div>
      <h2 className="lp-form-title">{L("Creá tu acceso", "Create your access")}</h2>
      <p className="lp-form-sub">
        {L(`${inv.condominio} te invitó a su portal. Completá tus datos para entrar.`,
           `${inv.condominio} invited you to its portal. Fill in your details to get in.`)}
      </p>

      {apiError && (
        <div className="lp-api-err">
          <i className="fa-solid fa-circle-exclamation" />
          {apiError}
        </div>
      )}

      <form onSubmit={enviar} noValidate>
        <TextField id="inv-email" label={L("Correo", "Email")} value={inv.email} readOnly icon="fa-envelope" />

        <div className="lp-row-2">
          <TextField id="inv-nombre" label={L("Nombre", "First name")} value={form.first_name}
            onChange={set("first_name")} error={errors.first_name} disabled={enviando}
            autoComplete="given-name" autoFocus />
          <TextField id="inv-apellido" label={L("Apellidos", "Last name")} value={form.last_name}
            onChange={set("last_name")} error={errors.last_name} disabled={enviando}
            autoComplete="family-name" />
        </div>

        <TextField id="inv-usuario" label={L("Usuario para iniciar sesión", "Username to sign in")}
          value={form.username} onChange={set("username")} error={errors.username}
          disabled={enviando} icon="fa-user" autoComplete="username" />

        <PasswordField id="inv-pass" label={L("Contraseña", "Password")} value={form.password}
          onChange={set("password")} error={errors.password} disabled={enviando}
          show={showPass} onToggle={() => setShowPass((v) => !v)}
          hint={L("Mínimo 8 caracteres. Evitá contraseñas comunes o solo números.",
                  "At least 8 characters. Avoid common or all-numeric passwords.")} />

        <PasswordField id="inv-pass2" label={L("Confirmar contraseña", "Confirm password")} value={form.confirmar}
          onChange={set("confirmar")} error={errors.confirmar} disabled={enviando}
          show={showPass} onToggle={() => setShowPass((v) => !v)} />

        <button type="submit" className="lp-btn" disabled={enviando}>
          {enviando
            ? <><i className="fa-solid fa-circle-notch lp-spin" /> {L("Creando acceso…", "Creating access…")}</>
            : L("Crear mi acceso", "Create my access")}
        </button>
      </form>

      <div style={{ textAlign: "center", marginTop: 14 }}>
        <Link to="/login" className="lp-link">{L("Ya tengo cuenta, iniciar sesión", "I already have an account")}</Link>
      </div>
    </AuthShell>
  );
}
